import { IArticle } from '@/api/interfaces/IArticle'
import { IFanQna } from '@/api/interfaces/IFanQna'
import { IPost } from '@/api/interfaces/IPost'
import { create } from 'zustand'

type StateType = 'FANBOARD' | 'ARTICLE' | 'QNA'

type State = {
  isLike: boolean
  likeCount: number
  isBookmark: boolean
  vote?: {
    candidates: IPost.VoteCandidateRO[]
    selectedId: number
    endDate: string
  }
}

const initialValue: State = {
  isLike: false,
  likeCount: 0,
  isBookmark: false,
}

const initialState: Record<StateType, Record<number, State | undefined>> = {
  FANBOARD: {},
  ARTICLE: {},
  QNA: {},
}

interface ClientState {
  state: Record<StateType, Record<number, State | undefined>>
  setState: (type: StateType, id: number, state: State) => void
}

export const useClientState = create<ClientState>()((set) => ({
  state: initialState,
  setState: (type: StateType, id: number, s: State | undefined) =>
    set(({ state }) => ({
      state: { ...state, [type]: { ...state[type], [id]: s } },
    })),
}))

export const useFanboardState = (id: number, initial: IPost.IPostRO) => {
  const state = useClientState((state) => state.state.FANBOARD[id])
  const setState = useClientState((state) => state.setState)

  return {
    state: state || {
      isBookmark: initial.isSaved ?? false,
      isLike: initial.isLike ?? false,
      likeCount: initial.likeCount ?? 0,
      vote: initial.voteCandidates
        ? {
            candidates: initial.voteCandidates,
            selectedId: initial.voteSelectedId,
            endDate: initial.voteEndDate,
          }
        : undefined,
    },
    set: (s: Partial<State>) => {
      setState('FANBOARD', id, {
        ...initialValue,
        ...(state || {}),
        ...s,
      })
    },
  }
}

export const useArticleState = (id: number, initial: IArticle.IArticleRO) => {
  const state = useClientState((state) => state.state.ARTICLE[id])
  const setState = useClientState((state) => state.setState)

  return {
    state: state || {
      isBookmark: initial.isBookmark ?? false,
      isLike: false,
      likeCount: 0,
    },
    set: (isBookmark: boolean) =>
      setState('ARTICLE', id, { isBookmark, isLike: false, likeCount: 0 }),
  }
}

export const useQnaState = (id: number, initial: IFanQna.IAnswerRO) => {
  const state = useClientState((state) => state.state.QNA[id])
  const setState = useClientState((state) => state.setState)

  return {
    state: state || {
      isBookmark: initial.isBookmark ?? false,
      isLike: initial.isLike ?? false,
      likeCount: initial.likeCount ?? 0,
    },
    set: (state: State) => setState('QNA', id, state),
  }
}
